import {
  ConnectionListResult,
  FeatureFlagsContext,
  GridSortProps,
  PaginatedQueryWrapper
} from '@tableau/data-catalog-shared';
import * as React from 'react';
import { Messages } from '../../../gen-src/Localize';
import {
  DatabaseDownstreamWorkbooksFilteredByTableQuery,
  DatabaseDownstreamWorkbooksFilteredByTableQuery_databases_tables_downstreamWorkbooksConnection_nodes,
  DatabaseDownstreamWorkbooksFilteredByTableQueryVariables,
} from '../../../gen-src/queries/DatabaseDownstreamWorkbooksFilteredByTableQuery';
import {
  DatabaseDownstreamWorkbooksFilteredByTableWithoutPersonalSpaceQuery,
  DatabaseDownstreamWorkbooksFilteredByTableWithoutPersonalSpaceQuery_databases_tables_downstreamWorkbooksConnection_nodes,
  DatabaseDownstreamWorkbooksFilteredByTableWithoutPersonalSpaceQueryVariables,
} from '../../../gen-src/queries/DatabaseDownstreamWorkbooksFilteredByTableWithoutPersonalSpaceQuery';
import {
  DatabaseDownstreamWorkbooksQuery,
  DatabaseDownstreamWorkbooksQuery_databases_downstreamWorkbooksConnection_nodes,
  DatabaseDownstreamWorkbooksQueryVariables,
} from '../../../gen-src/queries/DatabaseDownstreamWorkbooksQuery';
import {
  DatabaseDownstreamWorkbooksWithoutPersonalSpaceQuery,
  DatabaseDownstreamWorkbooksWithoutPersonalSpaceQuery_databases_downstreamWorkbooksConnection_nodes,
  DatabaseDownstreamWorkbooksWithoutPersonalSpaceQueryVariables,
} from '../../../gen-src/queries/DatabaseDownstreamWorkbooksWithoutPersonalSpaceQuery';
import { WorkbookSortOrder } from '../../../gen-src/queries/globalTypes';
import { DatabaseDownstreamWorkbooksFilteredByTableQueryGql } from '../../queries/DatabaseDownstreamWorkbooksFilteredByTableQueryGql';
import { DatabaseDownstreamWorkbooksFilteredByTableWithoutPersonalSpaceQueryGql } from '../../queries/DatabaseDownstreamWorkbooksFilteredByTableWithoutPersonalSpaceQueryGql';
import { DatabaseDownstreamWorkbooksQueryGql } from '../../queries/DatabaseDownstreamWorkbooksQueryGql';
import { DatabaseDownstreamWorkbooksWithoutPersonalSpaceQueryGql } from '../../queries/DatabaseDownstreamWorkbooksWithoutPersonalSpaceQueryGql';
import {
  getRowDataForWorkbook,
  getRowDataForWorkbookWithoutPersonalSpace,
  WorkbooksDisplayGrid,
  WorkbooksDisplayGridRowData
} from '../sharedDisplayGrid/WorkbooksDisplayGrid';
import { WorkbooksValidSortFields } from '../sorting/sortProviders/WorkbooksGridSortProvider';

type QueryVariables = DatabaseDownstreamWorkbooksQueryVariables;
type QueryResults = DatabaseDownstreamWorkbooksQuery;
type QueryResultListNode = DatabaseDownstreamWorkbooksQuery_databases_downstreamWorkbooksConnection_nodes;
type GridRowData = WorkbooksDisplayGridRowData;

type QueryVariablesWithoutPersonalSpace = DatabaseDownstreamWorkbooksWithoutPersonalSpaceQueryVariables;
type QueryResultsWithoutPersonalSpace = DatabaseDownstreamWorkbooksWithoutPersonalSpaceQuery;
type QueryResultListNodeWithoutPersonalSpace =
  DatabaseDownstreamWorkbooksWithoutPersonalSpaceQuery_databases_downstreamWorkbooksConnection_nodes;

type QueryVariablesWithFilter = DatabaseDownstreamWorkbooksFilteredByTableQueryVariables;
type QueryResultsWithFilter = DatabaseDownstreamWorkbooksFilteredByTableQuery;
type QueryResultListNodeWithFilter =
  DatabaseDownstreamWorkbooksFilteredByTableQuery_databases_tables_downstreamWorkbooksConnection_nodes;

type QueryVariablesWithFilterWithoutPersonalSpace = DatabaseDownstreamWorkbooksFilteredByTableWithoutPersonalSpaceQueryVariables;
type QueryResultsWithFilterWithoutPersonalSpace = DatabaseDownstreamWorkbooksFilteredByTableWithoutPersonalSpaceQuery;
type QueryResultListNodeWithFilterWithoutPersonalSpace =
  DatabaseDownstreamWorkbooksFilteredByTableWithoutPersonalSpaceQuery_databases_tables_downstreamWorkbooksConnection_nodes;

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  return resultData && resultData.databases && resultData.databases[0] &&
    resultData.databases[0].downstreamWorkbooksConnection;
}

function selectConnectionListResultWithoutPersonalSpace(resultData: QueryResultsWithoutPersonalSpace):
  ConnectionListResult<QueryResultListNodeWithoutPersonalSpace> | null {
  return resultData && resultData.databases && resultData.databases[0] &&
    resultData.databases[0].downstreamWorkbooksConnection;
}

function selectConnectionListResultForFilteredQuery(
  resultData: QueryResultsWithFilter
): ConnectionListResult<QueryResultListNodeWithFilter> | null {
  const table = resultData &&
    resultData.databases &&
    resultData.databases[0] &&
    resultData.databases[0].tables &&
    resultData.databases[0].tables[0];
  return table && table.downstreamWorkbooksConnection;
}

function selectConnectionListResultForFilteredQueryWithoutPersonalSpace(
  resultData: QueryResultsWithFilterWithoutPersonalSpace
): ConnectionListResult<QueryResultListNodeWithFilterWithoutPersonalSpace> | null {
  const table = resultData &&
    resultData.databases &&
    resultData.databases[0] &&
    resultData.databases[0].tables &&
    resultData.databases[0].tables[0];
  return table && table.downstreamWorkbooksConnection;
}

export type DatabaseDownstreamWorkbooksQueryGridProps =
  GridSortProps<WorkbooksValidSortFields> & {
    readonly databaseId: string;
    readonly filteredTableId: string | null;
    readonly rowCount: number | null;
  };

export function DatabaseDownstreamWorkbooksQueryGrid(props: DatabaseDownstreamWorkbooksQueryGridProps): JSX.Element {
  const { personalSpaceEnabled } = React.useContext(FeatureFlagsContext);
  const emptyGridMessage = Messages.EmptyDownstreamWorkbooksLineageGridMessage();
  const emptyFilteredGridMessage = Messages.EmptyDownstreamFilteredWorkbooksLineageGridMessage();


  return personalSpaceEnabled ? (
    props.filteredTableId ? (
      <PaginatedQueryWrapper<QueryVariablesWithFilter, QueryResultsWithFilter, QueryResultListNodeWithFilter, GridRowData>
        queryGql={DatabaseDownstreamWorkbooksFilteredByTableQueryGql}
        partialQueryVariables={{
          databaseId: props.databaseId,
          tableId: props.filteredTableId,
          orderBy: props.orderBy as WorkbookSortOrder
        }}
        selectConnectionListResult={selectConnectionListResultForFilteredQuery}
        mapResultNode={getRowDataForWorkbook}
      >
        {({ rowStore }) => <WorkbooksDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyFilteredGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />}
      </PaginatedQueryWrapper>
    ) : (
      <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
        queryGql={DatabaseDownstreamWorkbooksQueryGql}
        partialQueryVariables={{ databaseId: props.databaseId, orderBy: props.orderBy as WorkbookSortOrder }}
        selectConnectionListResult={selectConnectionListResult}
        mapResultNode={getRowDataForWorkbook}
      >
        {({ rowStore }) => <WorkbooksDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />}
      </PaginatedQueryWrapper>
    )
  ) : (
    props.filteredTableId ? (
      <PaginatedQueryWrapper<QueryVariablesWithFilterWithoutPersonalSpace, QueryResultsWithFilterWithoutPersonalSpace,
      QueryResultListNodeWithFilterWithoutPersonalSpace, GridRowData>
        queryGql={DatabaseDownstreamWorkbooksFilteredByTableWithoutPersonalSpaceQueryGql}
        partialQueryVariables={{
          databaseId: props.databaseId,
          tableId: props.filteredTableId,
          orderBy: props.orderBy as WorkbookSortOrder
        }}
        selectConnectionListResult={selectConnectionListResultForFilteredQueryWithoutPersonalSpace}
        mapResultNode={getRowDataForWorkbookWithoutPersonalSpace}
      >
        {({ rowStore }) => <WorkbooksDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyFilteredGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />}
      </PaginatedQueryWrapper>
    ) : (
      <PaginatedQueryWrapper<QueryVariablesWithoutPersonalSpace, QueryResultsWithoutPersonalSpace,
      QueryResultListNodeWithoutPersonalSpace, GridRowData>
        queryGql={DatabaseDownstreamWorkbooksWithoutPersonalSpaceQueryGql}
        partialQueryVariables={{ databaseId: props.databaseId, orderBy: props.orderBy as WorkbookSortOrder }}
        selectConnectionListResult={selectConnectionListResultWithoutPersonalSpace}
        mapResultNode={getRowDataForWorkbookWithoutPersonalSpace}
      >
        {({ rowStore }) => <WorkbooksDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />}
      </PaginatedQueryWrapper>
    )
  );
}
